import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Building2, Users, DollarSign, TrendingUp, Play, Database, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHART_COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalOrganizations: 0,
    economicDevOrgs: 0,
    totalDonations: 0,
    totalRevenue: 0
  });
  const [topDonors, setTopDonors] = useState([]);
  const [processingStatus, setProcessingStatus] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch donor analysis
      const donorAnalysis = await axios.get(`${API}/donors/analysis`);
      const { top_donors, organization_stats, total_donations } = donorAnalysis.data;
      
      setStats({
        totalOrganizations: organization_stats?.total_organizations || 0,
        economicDevOrgs: organization_stats?.economic_dev_orgs || 0,
        totalDonations: total_donations || 0,
        totalRevenue: organization_stats?.total_revenue || 0
      });
      
      setTopDonors(top_donors || []);
      
      // Fetch processing status
      const statusResponse = await axios.get(`${API}/processing-status`);
      setProcessingStatus(statusResponse.data || []);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const startProcessing = async (year) => {
    try {
      setIsProcessing(true);
      await axios.post(`${API}/process-data`, {
        year: year,
        revenue_threshold: 2000000,
        focus_economic_dev_only: true
      });
      
      toast.success(`Started processing IRS 990 data for ${year}`);
      
      // Refresh status after a short delay
      setTimeout(() => {
        fetchDashboardData();
        setIsProcessing(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error starting data processing:', error);
      toast.error('Failed to start data processing');
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-64"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const donorChartData = topDonors.slice(0, 5).map((donor, index) => ({
    name: donor._id?.substring(0, 20) || 'Anonymous',
    amount: donor.total_giving || 0,
    organizations: donor.organizations_count || 0
  }));

  return (
    <div className="min-h-screen p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight text-slate-900 mb-2">
          Economic Development
          <span className="block text-2xl font-normal text-slate-600 mt-1">
            Nonprofit Donor Intelligence
          </span>
        </h1>
        <p className="text-slate-600 max-w-2xl">
          Track donors and funding patterns for organizations focused on entrepreneurship, 
          business development, and economic empowerment. Showing all donors contributing $50,000+ 
          including foundations, corporations, impact investors, and government agencies.
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="high-contrast hover-lift transition-smooth" data-testid="total-orgs-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium tracking-widest uppercase text-slate-600">
              Economic Dev Orgs
            </CardTitle>
            <Building2 className="h-4 w-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold data-text">{stats.economicDevOrgs.toLocaleString()}</div>
            <p className="text-xs text-slate-600 mt-1">
              +{stats.totalOrganizations} total organizations
            </p>
          </CardContent>
        </Card>

        <Card className="high-contrast hover-lift transition-smooth" data-testid="total-donations-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium tracking-widest uppercase text-slate-600">
              Total Donations
            </CardTitle>
            <Users className="h-4 w-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold data-text">{stats.totalDonations.toLocaleString()}</div>
            <p className="text-xs text-slate-600 mt-1">
              Active donor relationships
            </p>
          </CardContent>
        </Card>

        <Card className="high-contrast hover-lift transition-smooth" data-testid="total-revenue-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium tracking-widest uppercase text-slate-600">
              Combined Revenue
            </CardTitle>
            <DollarSign className="h-4 w-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold data-text">
              ${(stats.totalRevenue / 1000000).toFixed(1)}M
            </div>
            <p className="text-xs text-slate-600 mt-1">
              Total organizational revenue
            </p>
          </CardContent>
        </Card>

        <Card className="high-contrast hover-lift transition-smooth" data-testid="avg-donation-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium tracking-widest uppercase text-slate-600">
              Avg Org Revenue
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold data-text">
              ${stats.economicDevOrgs > 0 ? ((stats.totalRevenue / stats.economicDevOrgs) / 1000000).toFixed(1) : 0}M
            </div>
            <p className="text-xs text-slate-600 mt-1">
              Per organization average
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Top Donors Chart */}
        <Card className="lg:col-span-2 high-contrast" data-testid="top-donors-chart">
          <CardHeader>
            <CardTitle className="text-lg font-bold">Top Donors by Total Giving</CardTitle>
            <p className="text-sm text-slate-600">
              All funders contributing $50K+ to economic development initiatives
            </p>
          </CardHeader>
          <CardContent>
            {donorChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={donorChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 12, fontFamily: 'JetBrains Mono' }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fontFamily: 'JetBrains Mono' }}
                    tickFormatter={(value) => `$${(value / 1000)}K`}
                  />
                  <Tooltip 
                    formatter={[(value) => [`$${value.toLocaleString()}`, 'Total Giving'], (value) => [value, 'Organizations Supported']]}
                    labelStyle={{ color: '#0F172A', fontFamily: 'Inter' }}
                  />
                  <Bar dataKey="amount" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-300 flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <Database className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No donor data available</p>
                  <p className="text-sm">Process IRS data to see donor analytics</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="high-contrast" data-testid="quick-actions-card">
          <CardHeader>
            <CardTitle className="text-lg font-bold">Quick Actions</CardTitle>
            <p className="text-sm text-slate-600">
              Process new data and explore insights
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              onClick={() => startProcessing(2022)}
              disabled={isProcessing}
              className="w-full"
              data-testid="process-2022-btn"
            >
              {isProcessing ? (
                <>Processing...</>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Process 2022 Data
                </>
              )}
            </Button>
            
            <Button 
              onClick={() => startProcessing(2023)}
              disabled={isProcessing}
              variant="outline"
              className="w-full"
              data-testid="process-2023-btn"
            >
              {isProcessing ? (
                <>Processing...</>
              ) : (
                <>
                  <Database className="h-4 w-4 mr-2" />
                  Process 2023 Data
                </>
              )}
            </Button>
            
            <div className="border-t pt-4">
              <Button 
                variant="ghost" 
                className="w-full justify-start"
                onClick={() => window.location.href = '/organizations'}
                data-testid="explore-orgs-btn"
              >
                <Search className="h-4 w-4 mr-2" />
                Explore Organizations
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Processing Status */}
      {processingStatus.length > 0 && (
        <Card className="high-contrast" data-testid="processing-status-card">
          <CardHeader>
            <CardTitle className="text-lg font-bold">Recent Processing Status</CardTitle>
            <p className="text-sm text-slate-600">
              Track data processing jobs and their completion status
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {processingStatus.slice(0, 5).map((status, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Badge 
                      variant={status.status === 'completed' ? 'default' : 
                              status.status === 'failed' ? 'destructive' : 'secondary'}
                    >
                      {status.status}
                    </Badge>
                    <div>
                      <p className="font-medium data-text">
                        {status.process_type} {status.year && `- ${status.year}`}
                      </p>
                      <p className="text-sm text-slate-600">
                        {status.economic_dev_orgs_found && 
                          `${status.economic_dev_orgs_found} economic dev orgs found`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm data-text text-slate-600">
                      {new Date(status.started_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
